import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { FrontendSettings } from '@common/interfaces/frontendSettings';

type FrontendSettingsActions = {
    addFrontendSettings: (settings: FrontendSettings) => void;
    frontendSettings: FrontendSettings;
};

export const FrontendSettingsContext = createContext<FrontendSettingsActions | undefined>(undefined);
FrontendSettingsContext.displayName = 'FrontendSettingsContext';

export const useFrontendSettings = () => {
    const context = useContext(FrontendSettingsContext);
    if (!context) {
        throw new Error('useFrontendSettings must be used within a FrontendSettingsProvider');
    }

    return context;
};

type FrontendSettingsProviderProps = {
    children: ReactNode;
    settings: FrontendSettings;
};

export const FrontendSettingsProvider = (props: FrontendSettingsProviderProps) => {
    const [frontendSettings, setFrontendSettings] = useState<FrontendSettings>(props.settings);

    const addFrontendSettings = useCallback(
        (settings: FrontendSettings) => {
            setFrontendSettings(settings);
        },
        [setFrontendSettings]
    );

    const contextValue = useMemo(
        () => ({
            addFrontendSettings,
            frontendSettings,
        }),
        [frontendSettings, addFrontendSettings]
    );

    return (
        <FrontendSettingsContext.Provider
            value={contextValue}
            {...props}
        />
    );
};
